<template>
    <v-container>
        <v-row>
            <v-col class="py-0">
                <v-label>{{$t('user.edit')}}</v-label>
            </v-col>
            <v-col class="py-0 text-right">
                <v-btn color="primary" @click.stop="showPassword=true">
                    {{$t('user.change_password')}}
                </v-btn>
            </v-col>
        </v-row>
        <v-form ref="form"
                v-model="isFormValid"
                v-if="user && !isUserPending">
            <v-container>
                <v-row>
                    <v-text-field outlined
                                  :label="$t('user.email')"
                                  :placeholder="$t('user.email')"
                                  v-model="user.email"
                                  :rules="emailRules" ></v-text-field>
                </v-row>

                <v-dialog
                    v-model="showPassword"
                    max-width="500"
                    persistent>
                    <v-card>
                        <v-card-title class="headline pb-10">Change Password</v-card-title>
                        
                        <v-card-text>
                            <v-spacer></v-spacer>
                            <v-form ref="form"
                                    v-model="isChangePasswordFormValid">

                                <v-text-field outlined
                                    :label="$t('user.password')"
                                    :placeholder="$t('user.password')"
                                    v-model="password"
                                    :rules="passwordRules"
                                    :append-icon="revealPasswordText ? 'mdi-eye' : 'mdi-eye-off'"
                                    :type="revealPasswordText ? 'text' : 'password'"
                                    @click:append="revealPasswordText = !revealPasswordText"
                                    v-on:input="changePassword"
                                    v-if="showPassword"
                                    ></v-text-field>

                                <v-text-field outlined
                                    ref="confirmPasswordEle"
                                    :label="$t('user.confirm_password')"
                                    :placeholder="$t('user.confirm_password')"
                                    v-model="confirmPassword"
                                    :rules="confirmPasswordRules"
                                    :append-icon="revealConfirmPasswordText ? 'mdi-eye' : 'mdi-eye-off'"
                                    :type="revealConfirmPasswordText ? 'text' : 'password'"
                                    @click:append="revealConfirmPasswordText = !revealConfirmPasswordText"
                                    v-if="showPassword"
                                    ></v-text-field>
                            </v-form>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn class="mb-5 mr-5"
                                @click="cancelChangePassword">
                                {{ $t('cancel') }}
                            </v-btn>

                            <v-btn class="mb-5 mr-5" color="primary" @click="savePassword" :disabled="!isChangePasswordFormValid">
                                {{$t('save')}}
                            </v-btn>
                        </v-card-actions>
                        </v-card>
                </v-dialog>

                <v-row>
                    <v-select outlined
                              :label="$t('user.role')"
                              :placeholder="$t('user.role')"
                              :items="permissionOptions"
                              v-model="user.permissions"
                              v-on:input="changePermission(user.permissions)" 
                              :rules="permissionRules" >
                    </v-select>
                </v-row>

                <v-row>
                    <v-select outlined
                              :label="$t('user.company')"
                              :placeholder="$t('user.company')"
                              :items="companyOptions"
                              v-model="user.companyId"
                              @change="changeCompany"
                              v-if="showCompany"
                              :rules="companyRules" >
                    </v-select>
                </v-row>

                <v-card class="mb-10" v-if="showEvent">
                    <v-card-title>
                        <!-- {{$t('event.guests')}}
                        <v-spacer></v-spacer>
                        <v-btn color="red" dark @click="getInvitedEvents" class="mr-3">
                            {{'revert'}}
                            <v-icon right>mdi-refresh</v-icon>
                        </v-btn>
                        <v-btn color="primary" @click="saveEventGuests">
                            {{$t('save')}}({{selectedEvents.length}})
                            <v-icon right>mdi-check</v-icon>
                        </v-btn> -->
                        {{$t('user.event')}}
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-text-field v-model="search"
                                            append-icon="mdi-magnify"
                                            :label="$t('search')"
                                            single-line
                                            hide-details>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-data-table :headers="eventHeaders"
                                    :items="eventOptions"
                                    :loading="isEventPending"
                                    :search="search"
                                    show-select
                                    v-model="selectedEvents"
                                    disable-sort>
                            <template v-slot:item.start_date="{value}">
                                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd')}}
                            </template>
                            <template v-slot:item.end_date="{value}">
                                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd')}}
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>

                <v-row>
                    <v-textarea outlined
                                  :auto-grow="true"
                                  :label="$t('user.remark')"
                                  :placeholder="$t('user.remark')"
                                  v-model="user.remark"></v-textarea>
                </v-row>
            </v-container>
        </v-form>
        <v-row>
            <v-col>
                <v-btn color="primary" @click="saveUser" :disabled="!isFormValid || !customFormValid || !callbackIsValid">
                    {{$t('save')}}
                </v-btn>
            </v-col>
            <v-col>
                <v-btn color="red" dark @click="deleteUser">
                    {{$t('delete')}}
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import {ref, computed, watch} from '@vue/composition-api'
    import {useFind, useGet} from "feathers-vuex";
    import eConfig from "../../evention-config";
    import {DateTime} from "luxon";
    import _ from 'lodash';

    export default {
        name: 'UserEdit',
        setup(props, {root}) {
            const {User, Company, Event, EventUser} = root.$FeathersVuex.api;

            const userId = computed(() => root.$route.params.userId);
            const {item: user, isPending: isUserPending} = useGet({model: User, id: userId});

            const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            const emailRules = ref([
                value => !!value.match(emailRegex) || 'Invalid Email Address',
            ]);
            const passwordRules = ref([
                value => !!value || 'Required',
                value => (value || '').length >= 8 || 'Min 8 characters',
                value => !!value.match(/[A-Za-z]/g) || 'Must contain at least one letter',
            ]);
            const permissionRules = ref([
                value => !!value || 'Required',
            ]);
            const confirmPasswordRules = ref([
                value => !!value || 'Required',
                value => (value == password.value) || 'Password Not Match',
            ]);
            const companyRules = ref([
                value => !!value || 'Required',
            ]);

            const confirmPasswordEle = ref(null);
            const isFormValid = ref(false);
            const customFormValid = ref(true);
            const showCompany = ref(false);
            const showEvent = ref(true);
            const callbackIsValid = ref(true);
            const ePermission = eConfig.user.permission;
            const permissionOptions = Object.values(ePermission);

            const password = ref("");
            const confirmPassword = ref("");
            const revealPasswordText = ref(false);
            const revealConfirmPasswordText = ref(false);
            const showPassword = ref(false);
            const isChangePasswordFormValid = ref(false);

            const savePassword = () => {
                root.$store.dispatch('users/patch', [userId.value, {password: password.value}]).then((user)=>{
                    // root.$store.dispatch('showSnackBar', root.$t('user.save_password_successful'));
                    user.password = "";
                    // password.value = "";
                    // confirmPassword.value = "";
                    root.$router.go();
                }, (e)=>{root.$handleFeatherError(e);} );
            }
            const cancelChangePassword = () => {
                showPassword.value = false;
                password.value = "";
                confirmPassword.value = "";
            }

            const saveUser = () => {
                delete user.value.password;

                switch (user.value.permissions) {
                    case ePermission.super:
                        user.value.companyId = null;
                        selectedEvents.value = [];
                        break;
                    case ePermission.company:
                        selectedEvents.value = [];
                        break;
                    case ePermission.event:
                        break;
                    default:
                        console.error("Different Permissions");
                        return;
                }
                
                let removedEvents = _.differenceWith(originalSelectedEvents.value, selectedEvents.value);
                let addedEvents = _.differenceWith(selectedEvents.value, originalSelectedEvents.value);

                user.value.save().then((user) => {
                    if (addedEvents.length > 0 || removedEvents.length > 0) {
                        saveEventUsers(addedEvents, removedEvents);
                    }
                    else
                        root.$router.replace({name: 'user-list'});
                }, (e)=>{root.$handleFeatherError(e);});
            };

            const saveEventUsers = async (addedEvents, removedEvents) => {
                try {
                    let removedEventUsers = [];
                    if (removedEvents.length > 0) {
                        await EventUser.find({
                            query: {
                                userId: userId.value,
                                eventId: {
                                    $in: removedEvents.map((e) => e.id),
                                }
                            }
                        }).then((result) => {
                            removedEventUsers = result.data;
                        });
                    }

                    await Promise.all([
                        ...addedEvents.map((e) => {
                            return new EventUser({userId: userId.value, eventId: e.id}).save()
                        }),
                        ...removedEventUsers.map((eg) => eg.remove()),
                    ]);

                    root.$router.replace({name: 'user-list'});
                } catch (e) {
                    root.$handleFeatherError(e);
                }
            };

            const deleteUser = () => {
                if (confirm(root.$t('user.confirm_delete'))) {
                    user.value.remove().then(() => {
                        root.$router.replace({name: 'user-list'});
                    }, root.$handleFeatherError.bind(root));
                }
            };

            const companyParams = computed(() => {
                return {
                    query: {
                        $sort: {
                            name: 1,
                        }
                    }
                }
            });
            const companyData = useFind({model: Company, params: companyParams});
            const companyOptions = computed(() => {
                return companyData.items.value.map(c => {
                    return {text: c.name, value: c.id}
                });
            });

            const changePermission = (inputPermission) => {
                switch (inputPermission) {
                    case ePermission.super:
                        showCompany.value = false;
                        showEvent.value = false;
                        break;
                    case ePermission.company:
                        showCompany.value = true;
                        showEvent.value = false;
                        break;
                    case ePermission.event:
                        showCompany.value = true;
                        showEvent.value = true;
                        break;
                    default:
                        return;
                }
                checkEventOfEventAdmin(inputPermission, selectedEvents.value.length);
            }

            const changePassword = ()=>{
                confirmPasswordEle.value.validate();
            };

            watch(isUserPending, (isUserPending, prevIsUserPending) => {
                if (!isUserPending) {
                    changePermission(user.value.permissions);
                    tempCompanyId.value = user.value.companyId;

                    if (user.value.permissions === ePermission.event)
                        getSelectedEvents();
                }
            });

            const tempCompanyId = ref(null);
            // const eventQueryWhen = computed(() => {
            //     return !isUserPending.value;
            // });
            const eventParams = computed(() => {
                return {
                    query: {
                        companyId: {
                            $in: [tempCompanyId.value], //user.value.companyId
                        },
                        $sort: {
                            name: 1,
                        }
                    }
                };
            });
            const {items: eventOptions, isPending: isEventPending} = useFind({
                model: Event,
                params: eventParams,
                // queryWhen: eventQueryWhen,
            });


            const eventHeaders = ref([
                {text: root.$t('event.name'), value: 'name'},
                {text: root.$t('event.start_date'), value: 'start_date'},
                {text: root.$t('event.end_date'), value: 'end_date'},
            ]);
            const originalSelectedEvents = ref([]);
            const selectedEvents = ref([]);
            const eventUserParams = computed(() => {
                return {
                    query: {
                        userId: userId.value,
                    }
                }
            });
            const getSelectedEvents = async () => {
                callbackIsValid.value = false;
                const {data: eventUsers} = await EventUser.find(eventUserParams.value);
                const eventIds = eventUsers.map((eg) => eg.eventId);
                const {data: events} = await Event.find({
                    query: {
                        id: {
                            $in: eventIds,
                        },
                    }
                });

                selectedEvents.value = Array.from(events);
                originalSelectedEvents.value = Array.from(events);
                callbackIsValid.value = true;
            };
            const changeCompany = () => {
                selectedEvents.value = [];
                tempCompanyId.value = user.value.companyId;
            }

            const checkEventOfEventAdmin = (permission, selectEventLength) => {
                if (permission === ePermission.event && selectEventLength === 0 ) {
                    customFormValid.value = false;
                } else
                    customFormValid.value = true;
            }

            watch(selectedEvents, (selectedEvents, prevSelectedEvents) => {
                if (user && user.value)
                    checkEventOfEventAdmin(user.value.permissions, selectedEvents.length);
            }, {deep:true});

            return {
                user,
                isUserPending,

                saveUser,
                deleteUser,
                
                isFormValid,
                customFormValid,
                callbackIsValid,

                emailRules,
                permissionRules,
                companyRules,
                passwordRules,
                confirmPasswordRules,
                
                revealPasswordText,
                revealConfirmPasswordText,

                permissionOptions,
                companyOptions,

                showPassword,
                showCompany,
                showEvent,

                changePermission,
                changePassword,

                password,
                confirmPassword,
                
                confirmPasswordEle,

                eventHeaders,
                isEventPending,
                search: ref(''),
                selectedEvents,
                DateTime,
                eventOptions,
                changeCompany,

                cancelChangePassword,
                savePassword,
                isChangePasswordFormValid,
            }
        },
    }
</script>